import {
	createContext,
	useContext,
	useEffect,
	useRef,
	useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { toast as showToast } from 'sonner'
import { notificationTitle } from './notification-labels.ts'

/**
 * One live arrival as the stream serializes it (mirror of the server
 * `NotificationEvent`). Kept local to the client so this module needs no
 * server-only import.
 */
type StreamNotification = {
	category: string
	type: string
	params: Record<string, unknown> | null
	linkTo: string | null
	actorId: string | null
	createdAt: string
}

type NotificationStream = {
	/**
	 * The SSE-derived unread count: `null` until the stream connects (the bell
	 * then falls back to the SSR/loader tally), the initial count once connected,
	 * incremented on each live arrival and decremented as the user reads.
	 */
	liveCount: number | null
	/** Keep {@link liveCount} coherent when the user marks rows read locally. */
	applyLocalRead: (kind: 'one' | 'all') => void
}

// Default value so a bell rendered without the provider (unit/route tests) reads
// a null live count and simply uses its loader data — no wrapper required.
const NotificationStreamContext = createContext<NotificationStream>({
	liveCount: null,
	applyLocalRead: () => {},
})

export function useNotificationStream() {
	return useContext(NotificationStreamContext)
}

/**
 * Opens the live notification stream (EPT-160, ADR-078) and shares it with the
 * bell, dropdown, and toast. Mounted once at the app root for a signed-in user:
 * it holds a single native `EventSource` (which reconnects on drop by itself),
 * tracks the live unread count, and on each arrival renders a Sonner toast and
 * an announcement in the one reused `aria-live` region below — except for events
 * the recipient caused themselves (`actorId === userId`), which bump the badge
 * silently. A guest (`userId == null`) opens no stream.
 */
export function NotificationStreamProvider({
	userId,
	children,
}: {
	userId: string | null
	children: React.ReactNode
}) {
	const { t } = useTranslation()
	const [liveCount, setLiveCount] = useState<number | null>(null)
	const [announcement, setAnnouncement] = useState('')

	// Read `t` through a ref so a language switch never tears down and reopens the
	// stream — the effect depends only on the identity of the signed-in user.
	const tRef = useRef(t)
	tRef.current = t

	useEffect(() => {
		if (!userId || typeof EventSource === 'undefined') return

		const source = new EventSource('/resources/notifications-stream')

		source.addEventListener('count', (event) => {
			setLiveCount(Number((event as MessageEvent).data))
		})

		source.addEventListener('notification', (event) => {
			const arrival = JSON.parse(
				(event as MessageEvent).data,
			) as StreamNotification
			setLiveCount((count) => (count ?? 0) + 1)

			// Suppress the toast + announcement for an event the recipient caused in
			// their own session (e.g. an admin editing their own roles); the badge
			// still bumps so the count stays truthful.
			if (arrival.actorId && arrival.actorId === userId) return

			const title = notificationTitle(tRef.current, arrival.type, arrival.params)
			showToast(title)
			// The title varies per arrival, so the live region re-announces even for
			// back-to-back notifications (identical text would be swallowed).
			setAnnouncement(tRef.current('notifications.live.arrival', { title }))
		})

		return () => source.close()
	}, [userId])

	return (
		<NotificationStreamContext.Provider
			value={{
				liveCount,
				applyLocalRead: (kind) =>
					setLiveCount((count) =>
						count == null ? count : kind === 'all' ? 0 : Math.max(0, count - 1),
					),
			}}
		>
			{children}
			{/* The single app-level polite live region (grounded spec): visually
			    hidden, announces each non-self arrival, silent on initial connect. */}
			<div className="sr-only" aria-live="polite" aria-atomic="true">
				{announcement}
			</div>
		</NotificationStreamContext.Provider>
	)
}
